import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import { apiUrl } from "../config/api";
import { userAuthHeaders } from "../utils/authHeaders";
import { handleUserAuthFailure } from "../utils/userPortalApi";

const shouldGroupMeltedTrade = (currentItem, nextItem) => {
  if (!currentItem || !nextItem) return false;

  const isCurrentMelted = String(currentItem.title || "").includes("آبشده");
  const isNextMelted = String(nextItem.title || "").includes("آبشده");

  return (
    isCurrentMelted &&
    isNextMelted &&
    currentItem.title === nextItem.title &&
    currentItem.description === nextItem.description &&
    currentItem.createdAt === nextItem.createdAt &&
    currentItem.type !== nextItem.type
  );
};

const groupTransactions = (items) => {
  const grouped = [];

  for (let index = 0; index < items.length; index += 1) {
    const currentItem = items[index];
    const nextItem = items[index + 1];

    if (shouldGroupMeltedTrade(currentItem, nextItem)) {
      const moneyItem = currentItem.type === "money" ? currentItem : nextItem;
      const goldItem = currentItem.type === "gold" ? currentItem : nextItem;

      grouped.push({
        id: `${currentItem.id}-${nextItem.id}`,
        title: currentItem.title,
        type: "combined",
        description: currentItem.description,
        createdAt: currentItem.createdAt,
        moneyAmount: moneyItem?.amount ?? "0",
        goldAmount: goldItem?.amount ?? "0",
        moneyBalanceAfter: moneyItem?.moneyBalanceAfter ?? currentItem.moneyBalanceAfter ?? nextItem.moneyBalanceAfter,
        goldBalanceAfter: goldItem?.goldBalanceAfter ?? currentItem.goldBalanceAfter ?? nextItem.goldBalanceAfter,
      });
      index += 1;
      continue;
    }

    grouped.push(currentItem);
  }

  return grouped;
};

const getMoneyImpactValue = (item) => {
  if (item.type === "combined") {
    return Number(item.moneyAmount || 0);
  }

  return item.type === "money" ? Number(item.amount || 0) : 0;
};

const getGoldImpactValue = (item) => {
  if (item.type === "combined") {
    return Number(item.goldAmount || 0);
  }

  return item.type === "gold" ? Number(item.amount || 0) : 0;
};

function UserBalanceDetial() {
  const ITEMS_PER_PAGE = 10;
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const userData = localStorage.getItem("user");

        if (!userData) {
          throw new Error("اطلاعات کاربر در دسترس نیست.");
        }

        const currentUser = JSON.parse(userData);

        if (!currentUser?.id) {
          throw new Error("شناسه کاربر در دسترس نیست.");
        }

        const response = await fetch(apiUrl(`/api/users/${currentUser.id}/balance`), {
          headers: userAuthHeaders(),
        });
        if (handleUserAuthFailure(response)) return;
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "خطا در دریافت اطلاعات کاربر");
        }

        setDetails(data);
      } catch (err) {
        setError(err.message || "خطا در دریافت اطلاعات کاربر");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const user = details?.user;
  const groupedTransactions = groupTransactions(details?.statement || details?.transactions || []);
  let moneyRunningBalance = Number(user?.moneyBalance || 0);
  let goldRunningBalance = Number(user?.goldBalance || 0);
  const transactions = groupedTransactions.map((item) => {
    const currentItem = {
      ...item,
      moneyBalanceAfter: moneyRunningBalance,
      goldBalanceAfter: goldRunningBalance,
    };

    moneyRunningBalance -= getMoneyImpactValue(item);
    goldRunningBalance -= getGoldImpactValue(item);

    return currentItem;
  });
  const totalPages = Math.max(1, Math.ceil(transactions.length / ITEMS_PER_PAGE));
  const paginatedTransactions = transactions.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatAmount = (value, maximumFractionDigits = 2) =>
    Number(value || 0).toLocaleString("fa-IR", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    });

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("fa-IR");
  };

  const formatImpact = (itemType, currentType, amount, unit) => {
    if (itemType !== currentType) {
      return `۰ ${unit}`;
    }

    return `${formatAmount(amount)} ${unit}`;
  };

  const getMoneyBalanceAfter = (item) => item.moneyBalanceAfter;

  const getGoldBalanceAfter = (item) => item.goldBalanceAfter;

  const getTransactionTypeLabel = (item) => {
    if (item.type === "combined") return "پولی و طلا";
    return item.type === "money" ? "پولی" : "طلا";
  };

  const getMoneyImpact = (item) => {
    if (item.type === "combined") {
      return `${formatAmount(item.moneyAmount)} ریال`;
    }

    return formatImpact(item.type, "money", item.amount, "ریال");
  };

  const getGoldImpact = (item) => {
    if (item.type === "combined") {
      return `${formatAmount(item.goldAmount)} گرم`;
    }

    return formatImpact(item.type, "gold", item.amount, "گرم");
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleDownloadPdf = async () => {
    if (!user || downloading) return;

    setDownloading(true);

    try {
      const container = document.createElement("div");
      container.dir = "rtl";
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.width = "900px";
      container.style.background = "#ffffff";
      container.style.color = "#111111";
      container.style.padding = "32px";
      container.style.fontFamily = "Tahoma, Arial, sans-serif";

      const rowsHtml =
        transactions.length > 0
          ? transactions
              .map(
                (item) => `
                  <tr>
                    <td style="border:1px solid #d7d7d7;padding:8px;text-align:center;">${item.title || "-"}</td>
                    <td style="border:1px solid #d7d7d7;padding:8px;text-align:center;">${getTransactionTypeLabel(item)}</td>
                    <td style="border:1px solid #d7d7d7;padding:8px;text-align:center;">
                      پولی: ${getMoneyImpact(item)}<br />
                      طلا: ${getGoldImpact(item)}
                    </td>
                    <td style="border:1px solid #d7d7d7;padding:8px;text-align:center;">${item.description || "-"}</td>
                    <td style="border:1px solid #d7d7d7;padding:8px;text-align:center;">
                      پولی: ${formatAmount(getMoneyBalanceAfter(item))} ریال<br />
                      طلا: ${formatAmount(getGoldBalanceAfter(item))} گرم
                    </td>
                    <td style="border:1px solid #d7d7d7;padding:8px;text-align:center;">${formatDate(item.createdAt)}</td>
                  </tr>
                `
              )
              .join("")
          : `
            <tr>
              <td colspan="6" style="border:1px solid #d7d7d7;padding:12px;text-align:center;">
                هیچ اطلاعاتی برای نمایش وجود ندارد.
              </td>
            </tr>
          `;

      container.innerHTML = `
        <div style="direction:rtl;text-align:right;">
          <h1 style="margin:0 0 16px;font-size:28px;">گزارش جزئیات اطلاعات و موجودی شما</h1>
          <div style="margin-bottom:24px;padding:16px;border:1px solid #d7d7d7;border-radius:12px;">
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px 24px;font-size:15px;">
              <div><strong>نام:</strong> ${user.firstName || "-"}</div>
              <div><strong>نام خانوادگی:</strong> ${user.lastName || "-"}</div>
              <div><strong>شماره تماس:</strong> ${user.phone || "-"}</div>
              <div><strong>مانده پولی:</strong> ${formatAmount(user.moneyBalance)} ریال</div>
              <div><strong>مانده طلا:</strong> ${formatAmount(user.goldBalance)} گرم</div>
              <div><strong>تعداد رکوردها:</strong> ${transactions.length}</div>
            </div>
          </div>

          <table style="width:100%;border-collapse:collapse;font-size:13px;">
            <thead>
              <tr>
                <th style="border:1px solid #d7d7d7;padding:10px;background:#f3f3f3;">موضوع</th>
                <th style="border:1px solid #d7d7d7;padding:10px;background:#f3f3f3;">نوع</th>
                <th style="border:1px solid #d7d7d7;padding:10px;background:#f3f3f3;">مقدار</th>
                <th style="border:1px solid #d7d7d7;padding:10px;background:#f3f3f3;">شرح</th>
                <th style="border:1px solid #d7d7d7;padding:10px;background:#f3f3f3;">موجودی جدید</th>
                <th style="border:1px solid #d7d7d7;padding:10px;background:#f3f3f3;">تاریخ</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </div>
      `;

      document.body.appendChild(container);

      const doc = new jsPDF({
        orientation: "p",
        unit: "pt",
        format: "a4",
      });

      await doc.html(container, {
        x: 20,
        y: 20,
        width: 555,
        windowWidth: 900,
      });

      doc.save("user-account-report.pdf");
      document.body.removeChild(container);
    } catch (downloadError) {
      alert("خطا در تولید فایل PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="MainPageTradesSection">
      <div className="MainPageSectionHeading">
        <span className="MainPagePanelEyebrow">حساب کاربری</span>
        <h3 className="MainPagePanelTitle">جزئیات اطلاعات و موجودی شما</h3>
      </div>

      <div className="UserPortalPanel">
        {loading ? (
          <p className="UserPortalEmpty">در حال دریافت اطلاعات...</p>
        ) : error ? (
          <p className="UserPortalEmpty UserPortalError">{error}</p>
        ) : (
          <>
            <div className="UserBalanceReportToolbar">
              <button
                type="button"
                className="UserBalanceReportBtn"
                onClick={handleDownloadPdf}
                disabled={downloading}
              >
                {downloading ? "در حال آماده‌سازی PDF..." : "دریافت PDF"}
              </button>
            </div>

            <div className="UserBalanceHistory">
              {transactions.length === 0 ? (
                <p className="UserPortalEmpty">هنوز ثبتی برای موجودی این حساب ذخیره نشده است.</p>
              ) : (
                <>
                  <div className="UserPortalTableWrap UserBalanceHistoryDesktop">
                    <table className="UserPortalTable">
                      <thead>
                        <tr>
                          <th>موضوع</th>
                          <th>نوع</th>
                          <th>مقدار</th>
                          <th>شرح</th>
                          <th>موجودی جدید</th>
                          <th>تاریخ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTransactions.map((item) => (
                          <tr key={item.id}>
                            <td>{item.title || "-"}</td>
                            <td>{getTransactionTypeLabel(item)}</td>
                            <td>
                              <div
                                className={
                                  item.type === "combined"
                                    ? Number(item.moneyAmount) >= 0
                                      ? "transaction-plus"
                                      : "transaction-minus"
                                    : Number(item.amount) >= 0
                                      ? "transaction-plus"
                                      : "transaction-minus"
                                }
                              >
                                پولی: {getMoneyImpact(item)}
                              </div>
                              <div
                                className={
                                  item.type === "combined"
                                    ? Number(item.goldAmount) >= 0
                                      ? "transaction-plus"
                                      : "transaction-minus"
                                    : item.type === "gold" && Number(item.amount) >= 0
                                      ? "transaction-plus"
                                      : item.type === "gold"
                                        ? "transaction-minus"
                                        : ""
                                }
                              >
                                طلا: {getGoldImpact(item)}
                              </div>
                            </td>
                            <td>{item.description || "-"}</td>
                            <td>
                              <div>پولی: {formatAmount(getMoneyBalanceAfter(item))} ریال</div>
                              <div>طلا: {formatAmount(getGoldBalanceAfter(item))} گرم</div>
                            </td>
                            <td>{formatDate(item.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="UserBalanceHistoryMobile">
                    {paginatedTransactions.map((item) => {
                      const isPositive = item.type === "combined"
                        ? Number(item.moneyAmount) >= 0 || Number(item.goldAmount) >= 0
                        : Number(item.amount) >= 0;
                      return (
                        <article key={item.id} className="UserBalanceImpactCard">
                          <div className="UserBalanceImpactTop">
                            <div className="UserBalanceImpactTitleRow">
                              <span
                                className={
                                  isPositive
                                    ? "UserBalanceImpactSign positive"
                                    : "UserBalanceImpactSign negative"
                                }
                              >
                                {isPositive ? "+" : "-"}
                              </span>
                              <strong>{item.title || "-"}</strong>
                            </div>
                            <span className="UserBalanceImpactDate">{formatDate(item.createdAt)}</span>
                          </div>

                          <p className="UserBalanceImpactDescription">{item.description || "-"}</p>

                          <div className="UserBalanceImpactDivider" />

                          <div className="UserBalanceImpactGrid">
                            <div className="UserBalanceImpactStat">
                              <span>تاثیر پولی</span>
                              <strong
                                className={
                                  item.type === "money" && isPositive
                                    ? "transaction-plus"
                                    : item.type === "money"
                                      ? "transaction-minus"
                                      : item.type === "combined" && Number(item.moneyAmount) >= 0
                                        ? "transaction-plus"
                                        : item.type === "combined" && Number(item.moneyAmount) < 0
                                          ? "transaction-minus"
                                      : ""
                                }
                              >
                                {getMoneyImpact(item)}
                              </strong>
                            </div>
                            <div className="UserBalanceImpactStat">
                              <span>تاثیر طلا</span>
                              <strong
                                className={
                                  item.type === "gold" && isPositive
                                    ? "transaction-plus"
                                    : item.type === "gold"
                                      ? "transaction-minus"
                                      : item.type === "combined" && Number(item.goldAmount) >= 0
                                        ? "transaction-plus"
                                        : item.type === "combined" && Number(item.goldAmount) < 0
                                          ? "transaction-minus"
                                      : ""
                                }
                              >
                                {getGoldImpact(item)}
                              </strong>
                            </div>
                          </div>

                          <div className="UserBalanceImpactDivider" />

                          <div className="UserBalanceImpactNewBalance">
                            <div className="UserBalanceImpactNewBalanceHeader">موجودی جدید</div>
                            <div className="UserBalanceImpactGrid">
                              <div className="UserBalanceImpactStat">
                                <span>موجودی پولی</span>
                                <strong>{formatAmount(getMoneyBalanceAfter(item))} ریال</strong>
                              </div>
                              <div className="UserBalanceImpactStat">
                                <span>موجودی طلا</span>
                                <strong>{formatAmount(getGoldBalanceAfter(item))} گرم</strong>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="TredeDetailPagination">
                      <button
                        type="button"
                        className="TredeDetailPageBtn"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        قبلی
                      </button>

                      <span className="TredeDetailPageInfo">
                        صفحه {currentPage} از {totalPages}
                      </span>

                      <button
                        type="button"
                        className="TredeDetailPageBtn"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        بعدی
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default UserBalanceDetial;
